import { supabase } from '../../shared/supabase/supabaseClient';

export type OrderConversationListItem = {
  id: string;
  order_id: string;
  branch_id: string;
  customer_id: string;
  status: string;
  created_at: string;
  closed_at: string | null;
  read_by_store_at: string | null;
  read_by_customer_at: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  last_message: string | null;
  last_message_at: string | null;
};

export type OrderMessage = {
  id: string;
  author: string;
  body: string;
  created_at: string;
};

export type OrderConversationFilter = 'all' | 'unread' | 'open' | 'closed';

export async function listOrderConversationsForOrg(
  orgId: string,
  filter: OrderConversationFilter = 'all',
  branchId?: string | null,
): Promise<OrderConversationListItem[]> {
  const { data, error } = await supabase.rpc('list_order_conversations_for_org', {
    p_org_id: orgId,
    p_limit: 50,
    p_offset: 0,
    p_filter: filter,
    p_branch_id: branchId ?? null,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as OrderConversationListItem[];
}

export async function countUnreadOrderConversations(orgId: string): Promise<number> {
  const { data, error } = await supabase.rpc('count_unread_order_conversations_for_org', {
    p_org_id: orgId,
  });
  if (error) throw new Error(error.message);
  const n = typeof data === 'number' ? data : Number(data ?? 0);
  return Number.isFinite(n) ? n : 0;
}

export async function getOrCreateOrderConversation(orderId: string) {
  const { data, error } = await supabase.rpc('get_or_create_order_conversation', {
    p_order_id: orderId,
  });
  if (error) throw new Error(error.message);
  const payload = data as {
    ok?: boolean;
    error?: string;
    conversation?: {
      id: string;
      order_id: string;
      status: string;
      closed_at: string | null;
    };
  };
  if (!payload?.ok || !payload.conversation) {
    throw new Error(payload?.error ?? 'No se pudo abrir la conversación');
  }
  return payload.conversation;
}

export async function listOrderMessages(conversationId: string): Promise<OrderMessage[]> {
  const { data, error } = await supabase
    .from('order_messages')
    .select('id, author, body, created_at')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as OrderMessage[];
}

export async function sendOrderMessageAsStore(conversationId: string, body: string): Promise<OrderMessage> {
  const { data, error } = await supabase.rpc('add_order_message', {
    p_conversation_id: conversationId,
    p_body: body,
  });
  if (error) throw new Error(error.message);
  const payload = data as { ok?: boolean; error?: string; message?: OrderMessage };
  if (!payload?.ok || !payload.message) {
    throw new Error(payload?.error ?? 'No se pudo enviar');
  }
  return payload.message;
}

export async function markOrderConversationReadByStore(conversationId: string): Promise<void> {
  await supabase.rpc('mark_order_conversation_read_by_store', {
    p_conversation_id: conversationId,
  });
}

export async function notifyOrderMessage(conversationId: string, messageId: string): Promise<void> {
  // BUG #6: was fire-and-forget with no error logging. Now logs on failure.
  // OBS #1: was passing body as JSON.stringify(...), now passes object directly
  // for consistency with the rest of the codebase.
  try {
    const { error } = await supabase.functions.invoke('notify-order-message', {
      body: {
        conversation_id: conversationId,
        message_id: messageId,
        author: 'store',
      },
    });
    if (error) {
      console.warn('[notifyOrderMessage] edge function error', error.message);
    }
  } catch (e) {
    console.warn('[notifyOrderMessage] failed to notify customer', e);
  }
}
